import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { getBillingStats } from '../utils/api';
import { formatDate } from '../utils/helpers';
import toast from 'react-hot-toast';
import { RefreshCw, TrendingUp, TrendingDown, DollarSign, AlertTriangle, Send } from 'lucide-react';

export default function BillingPage() {
  const navigate        = useNavigate();
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setStats(await getBillingStats()); }
    catch { toast.error('Failed to load billing stats'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <Layout title="Billing & MRR">
      <div className="loading-center"><div className="spinner" /> Loading…</div>
    </Layout>
  );

  const s       = stats || {};
  const growth  = s.mrr_growth || 0;
  const plans   = s.plan_breakdown || {};
  const expiring = s.expiring_trials || [];

  const planRows = [
    { label: 'Zeus Elite',      key: 'elite',      color: 'var(--purple)',     price: 159, badge: 'badge-purple' },
    { label: 'Zeus Pro',        key: 'pro',        color: 'var(--gold)',       price: 99,  badge: 'badge-gold'   },
    { label: 'Zeus Core',       key: 'core',       color: 'var(--blue)',       price: 59,  badge: 'badge-blue'   },
    { label: 'Early Bird',      key: 'early_bird', color: 'var(--green)',      price: 0,   badge: 'badge-green'  },
    { label: 'Trial',           key: 'trial',      color: 'var(--text-muted)', price: 0,   badge: 'badge-dim'   },
  ];

  return (
    <Layout title="Billing & MRR">

      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
        <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={13} /> Refresh</button>
      </div>

      {/* MRR Stat cards */}
      <div className="stat-grid" style={{ marginBottom: 24 }}>
        <div className="stat-card" style={{ '--stat-color': 'var(--gold)' }}>
          <span className="stat-label">Monthly Recurring Revenue</span>
          <span className="stat-value">${(s.mrr || 0).toLocaleString()}</span>
          <span className="stat-sub" style={{ color: growth >= 0 ? 'var(--green)' : 'var(--red)', display: 'flex', alignItems: 'center', gap: 4 }}>
            {growth >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
            {growth >= 0 ? '+' : ''}{growth}% vs last month
          </span>
        </div>

        <div className="stat-card" style={{ '--stat-color': 'var(--green)' }}>
          <span className="stat-label">New Firms This Month</span>
          <span className="stat-value">{s.new_this_month || 0}</span>
          <span className="stat-sub">of {s.total_orgs || 0} total</span>
        </div>

        <div className="stat-card" style={{ '--stat-color': growth < 0 ? 'var(--red)' : 'var(--blue)' }}>
          <span className="stat-label">Churn Rate</span>
          <span className="stat-value">{s.churn_rate || 0}%</span>
          <span className="stat-sub">{s.cancelled_this_month || 0} cancelled this month</span>
        </div>

        <div className="stat-card" style={{ '--stat-color': expiring.length > 0 ? 'var(--amber)' : 'var(--text-muted)' }}>
          <span className="stat-label">Expiring Trials (7d)</span>
          <span className="stat-value" style={{ color: expiring.length > 0 ? 'var(--amber)' : 'inherit' }}>
            {expiring.length}
          </span>
          <span className="stat-sub">need follow-up</span>
        </div>
      </div>

      <div className="two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

        {/* Plan breakdown */}
        <div className="card">
          <div className="card-header">
            <span className="card-title"><DollarSign size={12} style={{ marginRight: 5, verticalAlign: '-1px' }} />Plan Breakdown</span>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {planRows.map(p => {
              const count = plans[p.key] || 0;
              const total = Object.values(plans).reduce((a, b) => a + b, 0) || 1;
              const pct   = Math.round((count / total) * 100);
              const rev   = count * p.price;
              return (
                <div key={p.key} style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-dim)', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span className={`badge ${p.badge}`} style={{ width: 80, justifyContent: 'center' }}>{p.label}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: p.color, borderRadius: 2, transition: 'width 0.4s ease' }} />
                    </div>
                  </div>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--text-primary)', fontWeight: 700, width: 28, textAlign: 'right' }}>{count}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: rev > 0 ? 'var(--green)' : 'var(--text-muted)', width: 52, textAlign: 'right' }}>
                    {rev > 0 ? `$${rev.toLocaleString()}` : '—'}
                  </span>
                </div>
              );
            })}
            <div style={{ padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Total MRR</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.95rem', fontWeight: 700, color: 'var(--gold)' }}>${(s.mrr || 0).toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Expiring trials */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">
              <AlertTriangle size={12} style={{ marginRight: 5, verticalAlign: '-1px', color: expiring.length ? 'var(--amber)' : 'inherit' }} />
              Expiring Trials
            </span>
            {expiring.length > 0 && (
              <span className="badge badge-amber">{expiring.length} urgent</span>
            )}
          </div>
          {expiring.length === 0 ? (
            <div className="empty" style={{ padding: '40px 24px' }}>
              <AlertTriangle size={24} />
              <h3>No trials expiring soon</h3>
              <p>All trials have more than 7 days remaining.</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Firm</th>
                    <th>Expires</th>
                    <th>Days</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {expiring.sort((a, b) => a.days_left - b.days_left).map(t => (
                    <tr
                      key={t.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/orgs/${t.id}`)}
                    >
                      <td>
                        <div className="mono-sm">{t.id.slice(0, 8)}…</div>
                      </td>
                      <td className="mono-sm">{formatDate(t.trial_ends_at)}</td>
                      <td>
                        <span style={{
                          fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 700,
                          color: t.days_left <= 2 ? 'var(--red)' : t.days_left <= 4 ? 'var(--amber)' : 'var(--text-secondary)',
                        }}>
                          {t.days_left === 0 ? 'Today' : `${t.days_left}d`}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={e => { e.stopPropagation(); navigate(`/orgs/${t.id}`); }}
                          style={{ color: 'var(--gold)', borderColor: 'var(--border-gold)' }}
                        >
                          <Send size={11} /> Reach Out
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
